<div class="promise-to-pay-container">
	<!-- Filter Card -->
	<mat-card class="content-card filter-card">
		<mat-card-content>
			<div class="card-header">
				<h2 class="page-title">Promises to Pay</h2>

				<div class="header-actions">
					<button mat-stroked-button type="button" (click)="load()">
						<mat-icon>refresh</mat-icon>
						Refresh
					</button>
				</div>
			</div>

			<form [formGroup]="filterForm" (ngSubmit)="onSearch()">
				<formly-form
					[form]="filterForm"
					[fields]="filterFields"
					[model]="filterModel"
				></formly-form>

				<!-- Clear (x) button shared by every filter field, rendered in the matSuffix
				     slot. `field` is the formly field passed in as template context. -->
				<ng-template #clearSuffix let-field="field">
					<button
						*ngIf="field.formControl?.value"
						mat-icon-button
						type="button"
						aria-label="Clear filter"
						(click)="
							$event.stopPropagation(); field.formControl?.setValue(null)
						"
					>
						<mat-icon>close</mat-icon>
					</button>
				</ng-template>

				<div class="filter-actions">
					<button mat-stroked-button type="button" (click)="onReset()">
						<mat-icon>refresh</mat-icon>
						Reset
					</button>
					<button
						mat-raised-button
						color="primary"
						type="submit"
						[disabled]="isLoading"
					>
						<mat-spinner
							*ngIf="isLoading"
							diameter="18"
							class="btn-spinner"
						></mat-spinner>
						<mat-icon *ngIf="!isLoading">search</mat-icon>
						Search
					</button>
				</div>
			</form>
		</mat-card-content>
	</mat-card>

	<!-- Status summary -->
	<mat-card class="content-card summary-card" *ngIf="upcoming && isManualDebit">
		<mat-card-content>
			<!-- Requested promises by the day they fall due, laid out like the due-date
			     breakdown on a KapCharge batch and clickable the same way. -->
			<div class="upcoming-breakdown" *ngIf="upcoming as u">
				<div class="ub-head">
					<span class="ub-label">Requested, by due date</span>
					<span class="ub-hint">Click a day to filter the promises below</span>
				</div>

				<div class="ub-grid">
					<button
						type="button"
						class="ub-card"
						*ngFor="let day of u.buckets"
						[class.is-active]="activeUpcoming === day.date"
						[class.is-empty]="day.count === 0"
						[attr.aria-pressed]="activeUpcoming === day.date"
						(click)="selectUpcomingDay(day.date)"
					>
						<span class="ub-card-head">
							<span class="date-chip">
								<em>{{ dayLabel(day.date) }}</em> {{ dateLabel(day.date) }}
							</span>
							<mat-icon
								class="ub-active-icon"
								*ngIf="activeUpcoming === day.date"
								>filter_alt</mat-icon
							>
						</span>
						<span class="ub-count">
							<strong>{{ day.count }}</strong>
							promise{{ day.count === 1 ? '' : 's' }}
						</span>
						<span class="ub-amount">{{
							FormatUtil.formatCurrency(day.amount)
						}}</span>
					</button>

					<button
						type="button"
						class="ub-card ub-card-later"
						[class.is-active]="activeUpcoming === 'later'"
						[class.is-empty]="u.later.count === 0"
						[attr.aria-pressed]="activeUpcoming === 'later'"
						(click)="selectUpcomingLater()"
					>
						<span class="ub-card-head">
							<span class="date-chip">
								<em>{{ dateLabel(u.later.from) }}</em> and after
							</span>
							<mat-icon
								class="ub-active-icon"
								*ngIf="activeUpcoming === 'later'"
								>filter_alt</mat-icon
							>
						</span>
						<span class="ub-count">
							<strong>{{ u.later.count }}</strong>
							promise{{ u.later.count === 1 ? '' : 's' }}
						</span>
						<span class="ub-amount">{{
							FormatUtil.formatCurrency(u.later.amount)
						}}</span>
					</button>
				</div>
			</div>

		</mat-card-content>
	</mat-card>

	<!-- Results -->
	<mat-card class="content-card results-card">
		<mat-card-content>
			<div class="table-scroll">
				<table
					mat-table
					[dataSource]="rows"
					[trackBy]="trackById"
					class="flat-table"
				>
					<ng-container matColumnDef="loanNumber">
						<th mat-header-cell *matHeaderCellDef>Loan Number</th>
						<td mat-cell *matCellDef="let row">
							{{ row.loanNumber }}
						</td>
					</ng-container>

					<ng-container matColumnDef="customerName">
						<th mat-header-cell *matHeaderCellDef>Customer</th>
						<td mat-cell *matCellDef="let row">
							{{ row.customerName || '—' }}
						</td>
					</ng-container>

					<ng-container matColumnDef="type">
						<th mat-header-cell *matHeaderCellDef>Type</th>
						<td mat-cell *matCellDef="let row">{{ typeLabel(row) }}</td>
					</ng-container>

					<ng-container matColumnDef="amount">
						<th mat-header-cell *matHeaderCellDef class="numeric">Amount</th>
						<td mat-cell *matCellDef="let row" class="numeric">
							{{ FormatUtil.formatCurrency(row.amount) }}
						</td>
					</ng-container>

					<ng-container matColumnDef="promiseDate">
						<th mat-header-cell *matHeaderCellDef>Promise Date</th>
						<td mat-cell *matCellDef="let row">{{ row.promiseDate }}</td>
					</ng-container>

					<ng-container matColumnDef="status">
						<th mat-header-cell *matHeaderCellDef>Status</th>
						<td mat-cell *matCellDef="let row">
							<span
								class="status-badge"
								[style.--status-color]="
									PromiseToPayStatus.getColor(row.status)
								"
							>
								{{ row.status }}
							</span>
						</td>
					</ng-container>

					<ng-container matColumnDef="owner">
						<th mat-header-cell *matHeaderCellDef>Owner</th>
						<td mat-cell *matCellDef="let row">{{ row.ownerName || '—' }}</td>
					</ng-container>

					<ng-container matColumnDef="actions">
						<th mat-header-cell *matHeaderCellDef class="actions-header">
							Actions
						</th>
						<td mat-cell *matCellDef="let row">
							<mat-spinner
								*ngIf="isBusy(row); else rowActions"
								diameter="20"
							></mat-spinner>

							<ng-template #rowActions>
								<button
									mat-icon-button
									type="button"
									aria-label="More actions"
									[matMenuTriggerFor]="menu"
								>
									<mat-icon>more_vert</mat-icon>
								</button>

								<mat-menu #menu="matMenu">
									<button mat-menu-item (click)="onViewHistory(row)">
										<mat-icon>history</mat-icon>
										<span>History</span>
									</button>
									<button
										mat-menu-item
										*ngIf="canEdit(row)"
										(click)="onEdit(row)"
									>
										<mat-icon>edit</mat-icon>
										<span>Edit</span>
									</button>
									<button
										mat-menu-item
										*ngIf="canArrange(row)"
										(click)="onArrange(row)"
									>
										<mat-icon>check_circle</mat-icon>
										<span>Arrange</span>
									</button>
									<ng-container *ngIf="canCloseDeposit(row)">
										<button mat-menu-item (click)="onMarkProcessed(row)">
											<mat-icon>payments</mat-icon>
											<span>Processed</span>
										</button>
										<button mat-menu-item (click)="onMarkBroken(row)">
											<mat-icon>event_busy</mat-icon>
											<span>Broken</span>
										</button>
									</ng-container>
									<button
										mat-menu-item
										*ngIf="canCancel(row)"
										(click)="onCancel(row)"
									>
										<mat-icon>block</mat-icon>
										<span>Cancel</span>
									</button>
									<button
										mat-menu-item
										*ngIf="canDelete(row)"
										(click)="onDelete(row)"
									>
										<mat-icon>delete_outline</mat-icon>
										<span>Delete</span>
									</button>
								</mat-menu>
							</ng-template>
						</td>
					</ng-container>

					<tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
					<tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
				</table>
			</div>

			<div class="empty-state" *ngIf="!isLoading && rows.length === 0">
				<mat-icon>inbox</mat-icon>
				<p>No promises match these filters.</p>
			</div>

			<mat-paginator
				[length]="totalRows"
				[pageSize]="pageSize"
				[pageIndex]="currentPage"
				[pageSizeOptions]="[25, 50, 100]"
				(page)="onPageChange($event)"
				showFirstLastButtons
			></mat-paginator>
		</mat-card-content>
	</mat-card>
</div>
